import { useCallback, useSyncExternalStore } from 'react'

import { notifyStorageKeyChange, subscribeToStorageKey } from '@/utils/storage'

// useState-like hook bound to a boolean in localStorage, implemented as a
// tiny external store instead of a `useEffect` + `setState` hydration pair
// (react-hooks/set-state-in-effect): `useSyncExternalStore` returns
// `defaultValue` on the server and on the first client render (matching
// SSR, no hydration mismatch), then the real stored value once mounted.
// `setValue` writes straight to localStorage and notifies subscribers, so
// two components sharing a `storageKey` stay in sync.
//
// Failures (insecure context, disabled storage, JSON parse errors) are
// swallowed with a console.error so the component still works, it just
// doesn't persist.

const readStoredBoolean = (
  storageKey: string,
  defaultValue: boolean,
): boolean => {
  try {
    const stored = localStorage.getItem(storageKey)
    return stored !== null ? (JSON.parse(stored) as boolean) : defaultValue
  } catch (error) {
    console.error(`Failed to load ${storageKey} from localStorage:`, error)
    return defaultValue
  }
}

export function useLocalStorageBoolean(
  storageKey: string,
  defaultValue: boolean,
): [boolean, (next: boolean) => void] {
  const value = useSyncExternalStore(
    (onChange) => subscribeToStorageKey(storageKey, onChange),
    () => readStoredBoolean(storageKey, defaultValue),
    () => defaultValue,
  )

  const setValue = useCallback(
    (next: boolean) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(next))
      } catch (error) {
        console.error(`Failed to save ${storageKey} to localStorage:`, error)
      }
      notifyStorageKeyChange(storageKey)
    },
    [storageKey],
  )

  return [value, setValue]
}
